import { SimulationSet } from '../data/SimulationSet.js';
import { AggregatedFrame } from './AggregatedFrame.js';
import { Frame } from './Frame.js';
import RaysHandler from './RaysHandler.js';
import { layerStatus } from './status.js';

import { Pipeline, PipelineExecutionOptions, PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline.js';
import usePersistentStore, { addLayer, getLayerState, getVectorialScalarMode } from '@gui/store/persistent/persistentStore.js';
import { MIXED, SAVED, SIM_EMPTY, SIM_NOISE, SIM_SUN, SIM_SUN4D, SIM_TARGET, SIM_VVA, SIM_WIND } from '@visualizer/types';

export type Result = {
    unitOfMeasure: string;
    resultLabel: string;
    resultId: number;
    frameCollection: Frame[];
};
type SimulationKinds = number;

abstract class Layer {
    protected readonly UID: string;
    abstract name: string;
    protected readonly isNewLayerState: boolean;

    parentSimulationSet: SimulationSet;

    protected abstract readonly simKind: SimulationKinds;

    results: Result[] = [];
    raysHandler?: RaysHandler;

    aggregatedFrame: Frame | null = null;

    protected subSimulationId!: number;

    /**
     * @deprecated use zustand store instead
     */
    status: layerStatus = new layerStatus();

    // Added for the ResultSerializer
    frameMax: number = 0;

    protected pipeline;

    constructor(parentSimulationSet: SimulationSet, UID: string) {
        this.parentSimulationSet = parentSimulationSet;
        this.UID = UID;
        // Layer properties
        const hadPersistedState = Boolean(usePersistentStore.getState().layers.layerStates[this.UID]);
        addLayer(this.UID);
        this.isNewLayerState = !hadPersistedState;
        const oldState = getLayerState(this.UID);
        this.pipeline = new Pipeline(oldState.pipeline);
        // if (oldState.pipeline.blocks.length > 0) {
        //     console.log(this.pipeline, oldState.pipeline);
        // }
    }

    getPipeline() {
        return this.pipeline;
    }

    public runPipeline(options: PipelineExecutionOptions): PipelineExecutionResult {
        // console.log(this);
        const mode = getVectorialScalarMode();
        return this.getPipeline().execute(this.getActiveFrame().getSensors(), this.getResultDomain(mode), options);
    }

    getResultDomain(mode: 0 | 1): [number, number] {
        const domainOverride = getLayerState(this.getID()).resultDomainOverrides;
        if (domainOverride) {
            return mode === 0 ? domainOverride.vectorial : domainOverride.scalar;
        }
        const resultId = getLayerState(this.getID()).data?.activeResultId ?? 0;
        const result = this.results[resultId];
        let min = Infinity;
        let max = -Infinity;
        for (const frame of result.frameCollection) {
            const [frameMin, frameMax] = mode === 0 ? frame.getVectorialMinMax() : frame.getScalarMinMax();
            if (!Number.isFinite(frameMin) || !Number.isFinite(frameMax)) continue;
            if (frameMin < min) min = frameMin;
            if (frameMax > max) max = frameMax;
        }
        return [min, max];
    }

    public getId(): string {
        return this.UID;
    }

    public getUID() {
        return this.UID;
    }

    public getSimulationSetId(): SimulationSet['id'] {
        return this.parentSimulationSet.id;
    }

    setFrame(frameSel: number) {
        usePersistentStore.getState()?.setSegmentsPanel({ frameSelected: frameSel });
    }

    getFrame(frameSel: number) {
        const frame = this.getFrameCollection()[frameSel];
        if (!frame) {
            // console.warn(this);
            throw new Error(`Frame with index ${frameSel} not found`);
        }
        return frame;
    }
    getActiveFrame(): Frame {
        const segments = getLayerState(this.getID()).segments;
        if (segments.isAggregate && !this.aggregatedFrame) {
            this.activateAggregator();
        }
        const frame = segments.isAggregate ? this.aggregatedFrame : this.getFrame(segments.frameSelected);
        if (!frame) {
            throw new Error(`Active frame not found`);
        }
        return frame;
    }

    public getAggregateFrames(frameIndexes = getLayerState(this.getID()).segments.framesList): Frame[] {
        const selectedIndexes = new Set(frameIndexes);
        return this.getFrameCollection().filter((_, idx) => selectedIndexes.has(idx));
    }

    activateAggregator(sourceFrames = this.getAggregateFrames()) {
        if (this.aggregatedFrame instanceof AggregatedFrame) {
            this.aggregatedFrame.refreshFrames(sourceFrames);
            return;
        }
        this.aggregatedFrame = new AggregatedFrame(this, sourceFrames);
    }

    getResult() {
        // Use result from this layer's state, not from the active layer:
        const resultId = getLayerState(this.getID()).data?.activeResultId ?? 0;
        return this.results[resultId] ?? this.results[0];
    }

    getFrameCollection() {
        // Use result from this layer's state, not from the active layer:
        let resultId = getLayerState(this.getID()).data?.activeResultId ?? 0;
        if (this.results.length <= resultId) {
            resultId = 0;
        }
        return this.results[resultId].frameCollection;
    }

    // Added for Time/Energy Switch
    getFrameCollectionById(id: number) {
        return this.results?.[id]?.frameCollection ?? [];
    }

    /**
     *
     * @deprecated dont use it anymore
     * @returns
     */
    getStatus() {
        return this.status;
    }

    getID(): string {
        return this.UID;
    }
    public destroy() {
        // removeLayer(this.UID);
    }

    getSimKind(): SimulationKinds {
        const simKindOrder = [SIM_EMPTY, SIM_SUN, SIM_SUN4D, SIM_VVA, SIM_TARGET, SIM_NOISE, SIM_WIND, SAVED, MIXED];

        for (const simKind of simKindOrder) {
            if (this.isOfType(simKind)) return simKind;
        }

        return this.simKind;
    }

    isOfType(simKind: SimulationKinds): boolean {
        return (this.simKind & simKind) !== 0;
    }

    isSun(): boolean {
        return this.isOfType(SIM_SUN) || this.isOfType(SIM_SUN4D);
    }

    isSavedLayer(): boolean {
        return this.isOfType(SAVED);
    }
}

export default Layer;
